/**
 * API: Asistente IA en Expedientes (Sprint 17.2 / 17.3).
 *
 * Conversación contextual dentro de un expediente — gated por tier Pro
 * + cuota mensual (validado en backend). Audit log inmutable.
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

export interface AssistantMessageDTO {
  role: "user" | "assistant";
  content: string;
}

/**
 * Mensaje persistido del thread del asistente del expediente (Fase 3 demo).
 *
 * El BE persiste los últimos N turns en `assistant_thread_messages` para
 * que el panel cargue el contexto previo al abrir el expediente. La fecha
 * `created_at` viene serializada como ISO 8601 (`datetime` → JSON).
 */
export interface AssistantThreadMessageDTO {
  role: "user" | "assistant";
  content: string;
  /** ISO 8601 datetime. */
  created_at: string;
}

export interface AssistantToolCallDTO {
  name: string;
  is_error: boolean;
  output_preview: string | null;
}

export interface AssistantResponseDTO {
  audit_id: string;
  text: string;
  tool_calls: AssistantToolCallDTO[];
  model: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  truncated: boolean;
  /** Mensajes IA restantes este mes después de esta respuesta. */
  quota_remaining: number;
}

/**
 * Envía un mensaje al asistente del expediente. Backend ya valida
 * tier Pro + cuota; en 402 lanza ApiError con `detailData.reason`
 * apuntando al motivo machine-readable (`tier_locked_pro_required`,
 * `ai_quota_exhausted`).
 */
export function postExpedienteAssistantMessage(
  projectId: string,
  municipality: string,
  messages: AssistantMessageDTO[],
): Promise<AssistantResponseDTO> {
  return request<AssistantResponseDTO>(`/projects/${encodeURIComponent(projectId)}/assistant`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ municipality, messages }),
  });
}

/**
 * Carga el thread persistido del asistente para un expediente (Fase 3 demo).
 *
 * Devuelve los últimos N msgs en orden cronológico ASC, listos para
 * popular las bubbles iniciales del panel. Si el muni no tiene historial
 * aún, el endpoint responde `[]` — el panel arranca como antes.
 *
 * El endpoint también valida tenant + rol; si el usuario no tiene
 * acceso, devuelve 403 y dejamos que el caller decida el fallback (en el
 * panel: estado vacío silencioso).
 *
 * `municipality` (DANE) es obligatorio: el BE resuelve el tenant con él
 * via `ensure_context` y responde 422 si falta (#588).
 */
export function fetchAssistantThread(
  projectId: string,
  municipality: string,
): Promise<AssistantThreadMessageDTO[]> {
  return request<AssistantThreadMessageDTO[]>(
    `/projects/${encodeURIComponent(projectId)}/assistant/threads?municipality=${encodeURIComponent(municipality)}`,
  );
}

/** Persiste rating 👍/👎 sobre una respuesta del asistente. 204 si OK. */
export async function rateExpedienteAssistantResponse(args: {
  projectId: string;
  auditId: string;
  municipality: string;
  rating: 1 | -1;
  feedback?: string;
}): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const url = `${API_V1}/projects/${encodeURIComponent(args.projectId)}/assistant/${encodeURIComponent(args.auditId)}/rate`;
  const res = await safeFetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({
      municipality: args.municipality,
      rating: args.rating,
      feedback: args.feedback?.trim() || null,
    }),
  });
  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}
